import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Calculator, Layers, Upload, Variable } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FeedbackState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@h2j-data-team/ui";
import type {
  ConflictResolution,
  GroupDimensionWithSync,
  GroupMeasureWithSync,
  GroupVariableWithSync,
} from "@repo/shared";
import { PRODUCT_NAME } from "@repo/shared";

/** One item that differs from Qlik, reduced to what the card shows. */
interface ConflictCardItem {
  id: string;
  name: string;
  outOfSyncCount: number;
  sampleApp?: string;
  productValue: string;
  qlikValue: string;
}

export interface ConflictResolutionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variables: GroupVariableWithSync[];
  dimensions: GroupDimensionWithSync[];
  measures: GroupMeasureWithSync[];
  missingVariables: GroupVariableWithSync[];
  missingDimensions: GroupDimensionWithSync[];
  missingMeasures: GroupMeasureWithSync[];
  conflictResolutions: Record<string, ConflictResolution>;
  onResolutionChange: (next: Record<string, ConflictResolution>) => void;
  /** True while the sync or the validation before it runs: the confirm button shows progress. */
  isSyncing: boolean;
  /**
   * True only while the sync request itself runs: the dialog cannot be dismissed
   * then. The validation can take tens of seconds, and Cancel stays available
   * during it, as before the migration.
   */
  syncPending: boolean;
  onConfirm: () => void | Promise<void>;
}

const outOfSync = <T extends { status: string }>(statuses: T[]) =>
  statuses.filter((status) => status.status === "out_of_sync");

function SectionHeading({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <h3 className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[.08em] text-faint [&>svg]:size-3.5">
      {icon} {children}
    </h3>
  );
}

function ValueBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10.5px] font-semibold uppercase tracking-[.08em] text-faint">{label}</p>
      <pre className="mt-1 whitespace-pre-wrap rounded-input bg-muted p-3 font-mono text-xs text-ink">
        {value}
      </pre>
    </div>
  );
}

export function ConflictResolutionDialog({
  open,
  onOpenChange,
  variables,
  dimensions,
  measures,
  missingVariables,
  missingDimensions,
  missingMeasures,
  conflictResolutions,
  onResolutionChange,
  isSyncing,
  syncPending,
  onConfirm,
}: ConflictResolutionDialogProps) {
  const { t } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  const handleResolutionChange = (itemId: string, value: ConflictResolution) => {
    onResolutionChange({
      ...conflictResolutions,
      [itemId]: value,
    });
  };

  const totalConflicts =
    variables.length +
    dimensions.length +
    measures.length +
    missingVariables.length +
    missingDimensions.length +
    missingMeasures.length;

  const variableCards: ConflictCardItem[] = variables.map((variable) => {
    const statuses = outOfSync(variable.syncStatus);
    return {
      id: variable.id,
      name: variable.name,
      outOfSyncCount: statuses.length,
      sampleApp: statuses[0]?.appName,
      productValue: variable.definition,
      qlikValue: statuses[0]?.remoteDefinition ?? "",
    };
  });
  const dimensionCards: ConflictCardItem[] = dimensions.map((dimension) => {
    const statuses = outOfSync(dimension.syncStatus);
    return {
      id: dimension.id,
      name: dimension.title,
      outOfSyncCount: statuses.length,
      sampleApp: statuses[0]?.appName,
      productValue: dimension.fieldDefs.join(", "),
      qlikValue: statuses[0]?.remoteFieldDefs?.join(", ") ?? "",
    };
  });
  const measureCards: ConflictCardItem[] = measures.map((measure) => {
    const statuses = outOfSync(measure.syncStatus);
    return {
      id: measure.id,
      name: measure.title,
      outOfSyncCount: statuses.length,
      sampleApp: statuses[0]?.appName,
      productValue: measure.expression,
      qlikValue: statuses[0]?.remoteExpression ?? "",
    };
  });

  const conflictSection = (title: string, icon: ReactNode, items: ConflictCardItem[]) =>
    items.length > 0 && (
      <section className="flex flex-col gap-3" aria-label={title}>
        <SectionHeading icon={icon}>{title}</SectionHeading>
        {items.map((item) => (
          <div key={item.id} className="rounded-card border border-hairline p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium">{item.name}</p>
                <p className="text-xs text-dim">
                  {t("conflict.appsOutOfSync", { count: item.outOfSyncCount })}
                  {item.sampleApp ? t("conflict.sampleApp", { name: item.sampleApp }) : ""}
                </p>
              </div>
              <Select
                value={conflictResolutions[item.id] ?? "push"}
                onValueChange={(value) =>
                  handleResolutionChange(item.id, value as ConflictResolution)
                }
              >
                <SelectTrigger
                  className="w-60"
                  aria-label={t("conflict.resolutionFor", { name: item.name })}
                >
                  <SelectValue placeholder={t("conflict.choosePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="push">
                    {t("conflict.pushAction", { productName: PRODUCT_NAME })}
                  </SelectItem>
                  <SelectItem value="pull">
                    {t("conflict.pullAction", { productName: PRODUCT_NAME })}
                  </SelectItem>
                  <SelectItem value="skip">{t("conflict.skipAction")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <ValueBlock
                label={t("conflict.productLabel", { productName: PRODUCT_NAME })}
                value={item.productValue}
              />
              <ValueBlock label={t("conflict.qlikLabel")} value={item.qlikValue} />
            </div>
          </div>
        ))}
      </section>
    );

  const missingSection = (title: string, icon: ReactNode, items: { id: string; name: string }[]) =>
    items.length > 0 && (
      <section className="flex flex-col gap-3" aria-label={title}>
        <SectionHeading icon={icon}>{title}</SectionHeading>
        {items.map((item) => (
          <div key={item.id} className="rounded-card border border-hairline p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium">{item.name}</p>
                <p className="text-xs text-dim">{t("conflict.notFoundInQlik")}</p>
              </div>
              <Badge variant="info">{t("conflict.pushCreate")}</Badge>
            </div>
          </div>
        ))}
      </section>
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange} busy={syncPending}>
      <DialogContent size="lg" closeLabel={tc("close")}>
        <DialogHeader>
          <DialogTitle>{t("conflict.dialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("conflict.dialogDescription", { productName: PRODUCT_NAME })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {totalConflicts === 0 ? (
            <FeedbackState state="empty" title={t("conflict.noConflicts")} />
          ) : (
            <div className="flex flex-col gap-5">
              {conflictSection(t("conflict.variablesSection"), <Variable />, variableCards)}
              {conflictSection(t("conflict.dimensionsSection"), <Layers />, dimensionCards)}
              {conflictSection(t("conflict.measuresSection"), <Calculator />, measureCards)}
              {missingSection(
                t("conflict.variablesMissingSection"),
                <Variable />,
                missingVariables.map((variable) => ({ id: variable.id, name: variable.name })),
              )}
              {missingSection(
                t("conflict.dimensionsMissingSection"),
                <Layers />,
                missingDimensions.map((dimension) => ({ id: dimension.id, name: dimension.title })),
              )}
              {missingSection(
                t("conflict.measuresMissingSection"),
                <Calculator />,
                missingMeasures.map((measure) => ({ id: measure.id, name: measure.title })),
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("conflict.cancel")}</Button>
          </DialogClose>
          <Button
            onClick={() => void onConfirm()}
            disabled={totalConflicts === 0}
            loading={isSyncing}
          >
            {isSyncing ? (
              t("conflict.syncing")
            ) : (
              <>
                <Upload />
                {t("conflict.syncNow")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
